/**
 * Bundle widgets: <bundle-second-pair-widget> ("add a second pair of the same model in another colour and save") and
 * <bundle-accessory-widget> ("main product + accessory, e.g. slippers + insoles").
 * Rendered by snippets/bundle-widget.liquid (one shared markup); styles: assets/bundle-widget.css.
 *
 * `BundleWidget` holds everything the two widgets do identically: connect/disconnect lifecycle, sibling colour fetch +
 * cache, mirroring the main product form (selected variant, size unit toggler), rendering, size buttons, CTA state,
 * discount helpers and the single multi-item POST to /cart/add.js. It is not a registered custom element.
 *
 * First item = the variant selected in the main product form (via the theme's `variantChange` event on the section).
 * Second item = a sibling colour product (fetched, never navigated to) plus a size picked inside the widget.
 * The discount is display only; every string comes from the snippet's data JSON.
 *
 * Subclasses set `this.bundleType` (the `_bundle_type` line item property) and add only what really differs:
 * - second pair: the main product's own colour is a swatch, so it is seeded into the cache;
 * - accessory: auto-matches its size to the main size and links its cart line to the main line (`_upsell_for*`).
 */
(function () {
  'use strict';

  // snippets/bundle-widgets.liquid emits the script tag once per `bundle_widget` block, so the file can be loaded
  // more than once on a page: the elements are defined by the first copy only
  if (customElements.get('bundle-second-pair-widget') || customElements.get('bundle-accessory-widget')) return;

  // Numeric (EU) range each named size stands for, so main and accessory sizes can be matched in either direction
  // (main 41 -> accessory "Medium", main "Medium" -> accessory "41/42"). Keys: lowercased, letters only.
  var NAMED_SIZE_RANGES = {
    small: [37, 38],
    sm: [37, 38],
    s: [37, 38],
    medium: [41, 42],
    md: [41, 42],
    m: [41, 42],
    large: [43, 44],
    lg: [43, 44],
    l: [43, 44],
    extralarge: [45, 46],
    xlarge: [45, 46],
    xl: [45, 46]
  };

  var selectors = {
    data: '[data-bw-data]',
    swatch: '[data-bw-swatch]',
    sizes: '[data-bw-sizes]',
    size: '[data-bw-size]',
    thumb: '[data-bw-thumb]',
    placeholder: 'template[data-bw-placeholder]',
    colorName: '[data-bw-color-name]',
    unitPrice: '[data-bw-unit-price]',
    picker: '[data-bw-picker]',
    pickerHint: '[data-bw-picker-hint]',
    total: '[data-bw-total]',
    compare: '[data-bw-compare]',
    saved: '[data-bw-saved]',
    cta: '[data-bw-cta]',
    error: '[data-bw-error]',
    status: '[data-bw-status]',
    rowHint: '[data-bw-row-hint]',
    mainThumb: '[data-bw-main-thumb]',
    mainColor: '[data-bw-main-color]',
    mainSize: '[data-bw-main-size]',
    mainPrice: '[data-bw-main-price]',
    text: '[data-bw-text]',
    productSection: '[data-section-type="product"]',
    mainAddToCart: '[data-add-to-cart]',
    mainProductSelect: '[data-product-select]',
    mainSizeWrap: '.variant-input-wrap[data-is-size="true"]',
    activeConvertedSize: '.size-conversion__item.active:not(.hide)',
    sizeToggler: '.size-conversion__toggler',
    sizeTogglerLabel: '.js-toggle-size-conversion'
  };

  var SIZE_STORAGE_KEY = 'theme-size-tab';

  class BundleWidget extends HTMLElement {
    constructor() {
      super();

      this.data = null;
      this.strings = {};
      this.cache = new Map();
      this.inflight = new Map();
      this.requestToken = 0;
      this._readDiscount();

      // Second item (the sibling product / accessory shown in the widget)
      this.product = null; // normalized product currently displayed
      this.selectedHandle = null; // colour the user picked (may still be loading)
      this.desiredSize = null; // native size value picked or matched; kept across colour switches
      this.size = null; // desiredSize if the displayed product offers it in stock and it is shown in the active unit
      this.loading = false;
      this.loadFailed = false;
      this.adding = false;
      this.ready = false; // one-time setup done
      this.fallbackColorName = '';
      this.templates = {}; // copy templates containing {amount}, keyed by data-bw-text value
      this.conversion = {}; // size conversion data: formats, propertyNames, entries

      // Main product form
      this.firstVariant = null;
      this.variantTarget = null; // product section the main form's `variantChange` listener is bound to
      this.toggler = null;
      this.togglerObserver = null;

      this._onClick = this._onClick.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
      this._onWindowLoad = this._onWindowLoad.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      if (!this.ready) {
        // Broken data or markup must not leave a half-initialized element behind
        try {
          this.ready = this._setup();
        } catch (error) {
          console.warn(this.localName + ': setup failed', error);
        }
        if (!this.ready) return;
      }

      this.initialized = true;
      this.section = this.closest(selectors.productSection);
      // theme.Variants dispatches `variantChange` on its own product section (and `variant:change` on document for
      // every variant form of the page); only this section's form is the first item. The snippet is always rendered
      // inside a product section
      this.variantTarget = this.section;

      this.addEventListener('click', this._onClick);
      if (this.variantTarget) this.variantTarget.addEventListener('variantChange', this._onVariantChange);
      window.addEventListener('load', this._onWindowLoad);

      this._syncMainForm();
    }

    // One-time setup from the snippet's data JSON and markup (kept across a re-connect); false when it cannot run
    _setup() {
      var dataEl = this.querySelector(selectors.data);
      if (!dataEl) return false;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.warn(this.localName + ': invalid data JSON', error);
        return false;
      }

      // Prime the cache with the server-rendered products (no request for the default colour). The snippet always
      // renders the default colour's product, so a second item is displayed from the start
      Object.keys(this.data.products || {}).forEach((handle) => {
        this.cache.set(handle, this._normalizeProduct(this.data.products[handle]));
      });
      this.selectedHandle = this.data.selectedHandle;
      this.product = this.cache.get(this.selectedHandle);
      if (!this.product) {
        console.warn(this.localName + ': no data for the default product');
        return false;
      }

      // All copy is rendered by the snippet; placeholders: |size|, |color|
      this.strings = this.data.strings || {};
      this.conversion = this.data.sizeConversion || {};
      this.els = this._collectElements();
      this.firstProduct = this._normalizeProduct(this.data.firstProduct);
      this._prepare();

      // The server-rendered thumbnail and size grid already belong to the default product
      this.els.thumb.dataset.handle = this.product.handle;
      this.els.sizes.dataset.handle = this.product.handle;
      return true;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this.variantTarget) this.variantTarget.removeEventListener('variantChange', this._onVariantChange);
      this.variantTarget = null;
      window.removeEventListener('load', this._onWindowLoad);

      if (this.togglerObserver) {
        this.togglerObserver.disconnect();
        this.togglerObserver = null;
      }
      this.toggler = null;

      // Drop responses that are still in flight and go back to the colour that is displayed; the rest of the state
      // is kept for a re-connect
      this.requestToken++;
      this.loading = false;
      this.loadFailed = false;
      if (this.product) this.selectedHandle = this.product.handle;
      this.initialized = false;
    }

    _collectElements() {
      return {
        swatches: Array.from(this.querySelectorAll(selectors.swatch)),
        sizes: this.querySelector(selectors.sizes),
        thumb: this.querySelector(selectors.thumb),
        colorName: this.querySelector(selectors.colorName),
        unitPrice: this.querySelector(selectors.unitPrice),
        picker: this.querySelector(selectors.picker),
        pickerHint: this.querySelector(selectors.pickerHint),
        total: this.querySelector(selectors.total),
        compare: this.querySelector(selectors.compare),
        saved: this.querySelector(selectors.saved),
        cta: this.querySelector(selectors.cta),
        error: this.querySelector(selectors.error),
        status: this.querySelector(selectors.status),
        rowHint: this.querySelector(selectors.rowHint), // absent in the accessory row
        mainThumb: this.querySelector(selectors.mainThumb),
        mainColor: this.querySelector(selectors.mainColor),
        mainSize: this.querySelector(selectors.mainSize),
        mainPrice: this.querySelector(selectors.mainPrice),
        texts: Array.from(this.querySelectorAll(selectors.text))
      };
    }

    // Setup that needs `els`, `data` and the primed cache; subclasses extend it
    _prepare() {
      this.templates = this.data.templates || {};
      this.fallbackColorName = this.els.colorName.textContent.trim();

      // The server-rendered first pair photo is the product image
      this.els.mainThumb.dataset.src = this.firstProduct.image;
    }

    /*============================================================================
      Product data
    ==============================================================================*/

    // Accepts both the snippet's JSON and /products/<handle>.js
    _normalizeProduct(raw) {
      var optionNames = (raw.options || []).map((option) => (typeof option === 'string' ? option : option.name));
      var sizePosition = 0;
      optionNames.some((name, index) => {
        if (/size/i.test(name)) {
          sizePosition = index + 1;
          return true;
        }
        return false;
      });

      var sizes = [];
      if (sizePosition) {
        raw.variants.forEach((variant) => {
          var value = variant['option' + sizePosition];
          if (value == null) return;
          value = String(value);

          var entry = sizes.find((item) => item.value === value);
          if (!entry) {
            entry = { value: value, available: false };
            sizes.push(entry);
          }
          if (variant.available) entry.available = true;
        });
      }

      var firstAvailable = raw.variants.find((variant) => variant.available);
      var priceVariant = firstAvailable || raw.variants[0];

      return {
        handle: raw.handle,
        title: raw.title,
        image: this._imageUrl(raw.featured_image),
        variants: raw.variants,
        sizePosition: sizePosition,
        sizes: sizes,
        available: !!firstAvailable,
        displayPrice: priceVariant ? priceVariant.price : 0
      };
    }

    // URL of a product / variant image given as a URL string or as the /products/<handle>.js object
    _imageUrl(image) {
      if (image && typeof image === 'object') image = image.src || image.url;
      return image || '';
    }

    _fetchProduct(swatch) {
      var handle = swatch.dataset.handle;
      if (this.inflight.has(handle)) return this.inflight.get(handle);

      var request = fetch(swatch.dataset.url + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then((response) => {
          if (!response.ok) throw new Error('Product request failed');
          return response.json();
        })
        .then((raw) => {
          var product = this._normalizeProduct(raw);
          this.cache.set(handle, product);
          this.inflight.delete(handle);
          return product;
        })
        .catch((error) => {
          this.inflight.delete(handle);
          throw error;
        });

      this.inflight.set(handle, request);
      return request;
    }

    /*============================================================================
      Main product form
    ==============================================================================*/

    // Tracked state of the main form's variant; accepts the widget's variant JSON and the theme's `variantChange` payload
    _toFirstVariant(variant) {
      var size = null;
      var sizePosition = this._firstSizePosition();
      if (!this.data.singleSize && sizePosition) {
        var value = variant['option' + sizePosition];
        size = value == null ? null : String(value);
      }

      return {
        id: variant.id,
        price: variant.price,
        available: variant.available,
        image: this._imageUrl(variant.featured_image),
        size: size
      };
    }

    // Size option position (1-based) of the page product: the main form's own size marker first (the theme matches
    // the translated option name), then the option name
    _firstSizePosition() {
      var wrap = this.section && this.section.querySelector(selectors.mainSizeWrap);
      var match = wrap && /option(\d+)/.exec(wrap.dataset.index || '');
      return match ? parseInt(match[1], 10) : this.firstProduct.sizePosition;
    }

    // Catch-up for the initial `variantChange`, which fires before this script attaches its listener
    _readFirstVariantFromDom() {
      this.firstVariant = null;
      if (!this.section) return;

      var select = this.section.querySelector(selectors.mainProductSelect);

      if (this.data.singleSize) {
        // No size to choose: the variant the main form holds (it may still offer another option), else the one
        // the page rendered
        var variants = this.firstProduct.variants;
        var currentId = String(select && select.value ? select.value : this.data.firstVariantId);
        var current = variants.find((item) => String(item.id) === currentId) || variants[0];
        if (current) {
          this.firstVariant = this._toFirstVariant(current);
        }
        return;
      }

      var addToCart = this.section.querySelector(selectors.mainAddToCart);
      var wrapper = addToCart && addToCart.parentElement;
      // theme.js marks the wrapper with `select-size-step` until a size is chosen
      if (wrapper && wrapper.classList.contains('select-size-step')) return;

      if (!select || !select.value) return;

      var variant = this.firstProduct.variants.find((item) => String(item.id) === String(select.value));
      if (variant) {
        this.firstVariant = this._toFirstVariant(variant);
      }
    }

    _syncMainForm() {
      this._readFirstVariantFromDom();
      this._onFirstVariantUpdated();
      this._bindToggler();
      this._renderAll();
    }

    // Hook: runs after `firstVariant` changed (the accessory matches its size to the main size)
    _onFirstVariantUpdated() {}

    _onVariantChange(event) {
      // `variant` is null when no variant matches the selected options
      var variant = event.detail && event.detail.variant;
      this.firstVariant = variant ? this._toFirstVariant(variant) : null;
      this._onFirstVariantUpdated();
      this._renderAll();
    }

    _onWindowLoad() {
      // theme.js settles the main form's initial state (no size chosen yet etc.) after this element connects
      this._syncMainForm();
    }

    _bindToggler() {
      if (!this.section) return;

      var toggler = this.section.querySelector(selectors.sizeToggler);
      if (!toggler || toggler === this.toggler) return;

      if (this.togglerObserver) this.togglerObserver.disconnect();
      this.toggler = toggler;

      // theme.js can settle the active unit without a bubbling click, so watch the class attribute
      this.togglerObserver = new MutationObserver(() => this._onUnitChange());
      this.togglerObserver.observe(toggler, { attributes: true, attributeFilter: ['class'], subtree: true });
    }

    /*============================================================================
      Size units
    ==============================================================================*/

    // Format key (eu | us_men | us_women | uk) of the unit active in the main form
    _currentFormat() {
      var formats = this.conversion.formats || {};

      // No unit toggler in the main form (the product has no size conversions): always the native sizes,
      // whatever unit was saved on another product's page
      this._bindToggler();
      if (!this.toggler || !this.toggler.isConnected) return 'eu';

      var active = this.toggler.querySelector(selectors.sizeTogglerLabel + '.active');
      var label = active ? active.dataset.countryToggler : null;

      // Toggler present but not settled by theme.js yet: use the saved unit it is about to restore
      if (!label) {
        try {
          label = localStorage.getItem(SIZE_STORAGE_KEY);
        } catch (error) {
          label = null;
        }
      }
      if (!label) return 'eu';

      var normalized = String(label).trim().toLowerCase();
      return Object.keys(formats).find((key) => String(formats[key]).trim().toLowerCase() === normalized) || 'eu';
    }

    // Values of an EU size in the other units (us_men, us_women, uk), if the size has a conversion entry
    _conversionEntry(value) {
      return (this.conversion.entries || {})[value];
    }

    // Whether any size of the product has a value in another unit (US / UK); `entries` covers every sibling colour
    _hasConversion(product) {
      return product.sizes.some((size) => {
        var entry = this._conversionEntry(size.value);
        return Boolean(entry && (entry.us_men || entry.us_women || entry.uk));
      });
    }

    // Format used for the displayed second product's sizes: the unit of the main form, or the native sizes when
    // the product has no conversion entry. The main row always follows `_currentFormat()`.
    _sizeFormat() {
      return this._hasConversion(this.product) ? this._currentFormat() : 'eu';
    }

    // Displayed text of an EU size value in the given unit (falls back to the EU value)
    _sizeText(value, format) {
      if (format === 'eu') return value;
      var entry = this._conversionEntry(value);
      return (entry && entry[format]) || value;
    }

    _unitLabel(format) {
      return (this.conversion.formats || {})[format] || '';
    }

    // Same rule as theme.js' handleSizeConversionToggle: a size is shown in a unit only if it has a value in it
    _hasSizeInFormat(value, format) {
      if (format === 'eu') return true;
      var entry = this._conversionEntry(value);
      return Boolean(entry && entry[format]);
    }

    // "US Men 41" / "39": size text with its unit prefix (no prefix for EU)
    _sizeLabel(value, format) {
      return (format === 'eu' ? '' : this._unitLabel(format) + ' ') + this._sizeText(value, format);
    }

    // "Size 41" / "Size US Men 8": a size in the given unit (`template` defaults to rowHintSize, `format` to the unit
    // active in the main form)
    _sizeHint(value, template, format) {
      return (template || this.strings.rowHintSize).replace('|size|', this._sizeLabel(value, format || this._currentFormat()));
    }

    // Whether a size of the displayed product can be chosen: in stock and shown in the active unit
    _sizeSelectable(value) {
      var entry = this.product.sizes.find((item) => item.value === value);
      return !!entry && entry.available && this._hasSizeInFormat(value, this._sizeFormat());
    }

    // The main form's size unit changed: the wanted size is chosen only while the active unit shows it (it is
    // dropped when the new unit has no value for it and comes back when a unit that shows it is active again)
    _onUnitChange() {
      if (this.desiredSize && this._needsSize()) {
        this.size = this._sizeSelectable(this.desiredSize) ? this.desiredSize : null;
      }
      this._renderAll();
    }

    _renderSizeLabels() {
      var format = this._sizeFormat();
      var unit = this._unitLabel(format);

      Array.from(this.els.sizes.querySelectorAll(selectors.size)).forEach((button) => {
        var text = this._sizeText(button.dataset.size, format);
        button.classList.toggle('hide', !this._hasSizeInFormat(button.dataset.size, format));
        button.textContent = text;
        var label = (unit ? unit + ' ' : '') + text;
        if (button.disabled) label += ', ' + this.strings.soldOutSuffix;
        button.setAttribute('aria-label', label);
      });
    }

    /*============================================================================
      Colour / size selection
    ==============================================================================*/

    _onClick(event) {
      var swatch = event.target.closest(selectors.swatch);
      if (swatch && this.contains(swatch)) {
        this._selectColor(swatch);
        return;
      }

      var sizeButton = event.target.closest(selectors.size);
      if (sizeButton && this.contains(sizeButton)) {
        this._selectSize(sizeButton.dataset.size);
        return;
      }

      var cta = event.target.closest(selectors.cta);
      if (cta && this.contains(cta)) {
        this._addToCart();
      }
    }

    _selectColor(swatch) {
      var handle = swatch.dataset.handle;
      if (swatch.disabled || this.adding) return;
      if (handle === this.selectedHandle && !this.loadFailed) return;

      this.selectedHandle = handle;
      this.loadFailed = false;
      this._clearError();

      // Latest click wins: older responses see a different token and are dropped
      var token = ++this.requestToken;
      var cached = this.cache.get(handle);

      if (cached) {
        this.loading = false;
        this._setProduct(cached);
        return;
      }

      this.loading = true;
      this._renderAll();

      this._fetchProduct(swatch)
        .then((product) => {
          if (token !== this.requestToken) return;
          this.loading = false;
          this._setProduct(product);
        })
        .catch((error) => {
          if (token !== this.requestToken) return;
          console.warn(this.localName + ': could not load color', error);
          this.loading = false;
          this.loadFailed = true;
          // Fall back to the colour that is still displayed
          this.selectedHandle = this.product.handle;
          this._renderAll();
          this._showError(this.strings.errorLoad);
        });
    }

    // Keep the picked size when the (new) colour has it in stock; otherwise clear it and tell the user
    _applyDesiredSize() {
      this.size = null;
      if (!this._needsSize() || !this.desiredSize) return;

      if (this._sizeSelectable(this.desiredSize)) this.size = this.desiredSize;
    }

    // Returns whether the size was applied
    _selectSize(value) {
      if (this.loading || this.adding || !this._sizeSelectable(value)) return false;

      this.desiredSize = value;
      this.size = value;
      this._clearError();
      this._renderAll();
      return true;
    }

    // Sizes are asked for only when the displayed product has a size option with more than one variant
    _needsSize() {
      return !!this.product.sizePosition && this.product.variants.length > 1;
    }

    _setProduct(product) {
      this.product = product;
      this._applyDesiredSize();
      this._renderAll(true);
    }

    // Variant of the displayed product for the chosen size (first available one when there is nothing to choose)
    _secondVariant() {
      if (!this._needsSize()) {
        return this.product.variants.find((variant) => variant.available) || null;
      }
      if (!this.size) return null;

      var key = 'option' + this.product.sizePosition;
      return this.product.variants.find((variant) => String(variant[key]) === this.size && variant.available) || null;
    }

    /*============================================================================
      Rendering
    ==============================================================================*/

    _currentColorName() {
      var swatch = this.els.swatches.find((item) => item.dataset.handle === this.selectedHandle);
      return swatch ? swatch.dataset.color : this.fallbackColorName;
    }

    _renderAll(rebuildSizes) {
      this._renderSwatches();
      this._renderMainRow();
      this._renderProductRow();
      this.els.sizes.hidden = !this._needsSize();
      this._renderSizeGrid(rebuildSizes);
      this._renderPrices();
      this._renderHints();
      this._renderCta();
      this._renderPickerState();
      this._renderStatus();
    }

    // First pair row: the page product with the variant selected in the main form
    _renderMainRow() {
      var variant = this.firstVariant;

      // Photo: selected variant, else the product image
      var src = (variant && variant.image) || this.firstProduct.image;
      if (this.els.mainThumb.dataset.src !== src) {
        this.els.mainThumb.dataset.src = src;
        this._renderThumb(this.els.mainThumb, src, this.firstProduct.title);
      }

      // Colour of the page product, or its full title when the title has no colour part
      this.els.mainColor.textContent = this.data.mainColor || this.firstProduct.title;

      // Size line: the size selected in the main form (with a sold-out note when it applies), a prompt before a size
      // is chosen, or nothing
      var sizeText = '';
      if (variant && variant.size) {
        sizeText = this._sizeHint(variant.size, variant.available ? null : this.strings.rowHintSizeSoldOut);
      } else if (variant) {
        sizeText = variant.available ? '' : this.strings.rowHintSoldOut;
      } else if (!this.data.singleSize) {
        sizeText = this.strings.rowHintNeedSize;
      }
      this._setText(this.els.mainSize, sizeText);
      this.els.mainSize.hidden = !sizeText;

      var price = variant ? variant.price : this.data.firstFallbackPrice;
      this.els.mainPrice.textContent = this._formatMoney(price);
    }

    _renderPrices() {
      var secondVariant = this._secondVariant();
      var secondBase = secondVariant ? secondVariant.price : this.product.displayPrice;
      var firstPrice = this.firstVariant ? this.firstVariant.price : this.data.firstFallbackPrice;
      // The first item keeps its full price, so the second item's row is whatever is left of the bundle total
      var secondDiscounted = Math.max(0, this._bundleTotal(firstPrice, secondBase) - firstPrice);

      this.els.unitPrice.textContent = this._formatMoney(secondDiscounted);
      var saved = this._renderTotals(firstPrice, secondBase);

      // Copy that mentions the amount saved ({amount}) follows the selection
      var amountText = this._formatMoneyShort(saved);
      this.els.texts.forEach((element) => {
        var template = this.templates[element.dataset.bwText];
        if (template && template.indexOf('{amount}') !== -1) {
          element.textContent = template.replace(/\{amount\}/g, amountText);
        }
      });
    }

    // Money for copy such as "Save €7": whole amounts without decimals
    _formatMoneyShort(cents) {
      var format = theme.settings.moneyFormat;
      if (cents % 100 === 0) {
        format = String(format)
          .replace(/\{\{\s*amount\s*\}\}/, '{{amount_no_decimals}}')
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, '{{amount_no_decimals_with_comma_separator}}');
      }
      return theme.Currency.formatMoney(cents, format);
    }

    // Both hints describe the second item only; the first pair's state is in its own row. The row hint is optional
    // (the accessory row has none), the picker hint is empty once everything is chosen
    _renderHints() {
      var color = this._currentColorName();
      var rowHint;
      var pickerHint;

      if (this.loading) {
        rowHint = this.strings.rowHintLoading;
        pickerHint = this.strings.pickerHintLoading.replace('|color|', color);
      } else if (!this.product.available) {
        rowHint = this.strings.rowHintSoldOut;
        pickerHint = this.strings.pickerHintSoldOut.replace('|color|', color);
      } else {
        var format = this._sizeFormat();

        if (this.size) {
          rowHint = this._sizeHint(this.size, null, format);
        } else {
          rowHint = this._needsSize() ? this.strings.rowHintPickSize : '';
        }

        // "Not available in this colour" only for a size the active unit shows; a size the unit has no value for
        // is simply not offered, so the prompt asks for a size
        if (this._needsSize() && !this.size && this.desiredSize && this._hasSizeInFormat(this.desiredSize, format)) {
          pickerHint = this.strings.pickerHintSizeUnavailable
            .replace('|size|', this._sizeLabel(this.desiredSize, format))
            .replace('|color|', color);
        } else if (this._needsSize() && !this.size) {
          pickerHint = this.strings.pickerHintPickSize;
        } else {
          // Fully chosen: nothing left to prompt for
          pickerHint = '';
        }
      }

      if (this.els.rowHint) {
        this._setText(this.els.rowHint, rowHint);
        this.els.rowHint.hidden = !rowHint;
      }
      this._setText(this.els.pickerHint, pickerHint);
      this.els.pickerHint.hidden = !pickerHint;
    }

    // Writes text only when it changed, so unchanged content is not announced again
    _setText(element, text) {
      if (element.textContent !== text) element.textContent = text;
    }

    // Screen reader status: the prompt in the picker, or the CTA state when there is nothing left to prompt for.
    // The visible hints are shown and hidden, which a live region does not announce reliably
    _renderStatus() {
      if (!this.els.status) return;
      this._setText(this.els.status, this.els.pickerHint.textContent || this.els.cta.textContent);
    }

    _renderSwatches() {
      this.els.swatches.forEach((swatch) => {
        var selected = swatch.dataset.handle === this.selectedHandle;
        swatch.setAttribute('aria-pressed', selected ? 'true' : 'false');
        swatch.classList.toggle('is-selected', selected);
      });
    }

    // Colour name and thumbnail of the displayed product
    _renderProductRow() {
      var colorName = this._currentColorName();
      if (colorName) this.els.colorName.textContent = colorName;

      if (this.els.thumb.dataset.handle === this.product.handle) return;

      // Thumbnail follows the displayed product (kept while the next colour is loading)
      this.els.thumb.dataset.handle = this.product.handle;
      this._renderThumb(this.els.thumb, this.product.image, this.product.title);
    }

    // 56px thumbnail from a product/variant image URL; an empty `src` shows the snippet's placeholder
    _renderThumb(container, src, alt) {
      if (!src) {
        var placeholder = this.querySelector(selectors.placeholder);
        if (placeholder) {
          container.replaceChildren(placeholder.content.cloneNode(true));
        } else {
          container.replaceChildren();
        }
        return;
      }

      try {
        var url = new URL(src, window.location.origin);
        url.searchParams.set('width', '160');
        src = url.toString();
      } catch (error) {
        // Keep the original URL if it cannot be parsed
      }

      var img = document.createElement('img');
      img.src = src;
      img.alt = alt;
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      container.replaceChildren(img);
    }

    _renderSizeGrid(rebuild) {
      var stale = this.els.sizes.dataset.handle !== this.product.handle;
      if (rebuild || !this.els.sizes.children.length || stale) {
        this._renderSizes();
      } else {
        this._renderSizeLabels();
        this._renderSizeSelection();
      }
    }

    _renderSizes() {
      var product = this.product;
      var container = this.els.sizes;
      container.replaceChildren();
      container.dataset.handle = product.handle;

      product.sizes.forEach((entry) => {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'bundle-widget__size';
        button.dataset.bwSize = '';
        button.dataset.size = entry.value;
        if (!entry.available) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        }
        container.appendChild(button);
      });

      this._renderSizeLabels();
      this._renderSizeSelection();
    }

    _renderSizeSelection() {
      Array.from(this.els.sizes.querySelectorAll(selectors.size)).forEach((button) => {
        var selected = !!this.size && button.dataset.size === this.size;
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.classList.toggle('is-selected', selected);
      });
    }

    // Writes the totals row for the undiscounted prices of both items; returns the amount saved
    _renderTotals(first, second) {
      var sum = first + second;
      var total = this._bundleTotal(first, second);

      this._setText(this.els.total, this._formatMoney(total));
      this._setText(this.els.compare, this._formatMoney(sum));
      this._setText(this.els.saved, this._formatMoney(sum - total));
      return sum - total;
    }

    _renderPickerState() {
      this.els.picker.classList.toggle('is-loading', this.loading);
      this.els.picker.setAttribute('aria-busy', this.loading ? 'true' : 'false');
    }

    // Returns the CTA label and whether the bundle can be added
    _ctaState() {
      var firstChosen = !!this.firstVariant;
      var needsSize = this._needsSize();

      if (this.adding) return { text: this.strings.ctaAdding, ready: false };
      if (this.loading) return { text: this.strings.ctaLoading, ready: false };
      if (!this.product.available) return { text: this.strings.ctaColorSoldOut, ready: false };
      if (!firstChosen && needsSize && !this.size) return { text: this.strings.ctaPickBoth, ready: false };
      if (!firstChosen) return { text: this.strings.ctaNeedFirstSize, ready: false };
      if (!this.firstVariant.available) return { text: this.strings.ctaFirstSoldOut, ready: false };
      if (needsSize && !this.size) return { text: this.strings.ctaNeedSecondSize, ready: false };
      if (!this._secondVariant()) return { text: this.strings.ctaColorSoldOut, ready: false };

      return { text: this.strings.ctaReady, ready: true };
    }

    _renderCta() {
      var state = this._ctaState();
      var cta = this.els.cta;

      this._setText(cta, state.text);
      // Not `disabled` while adding: that would drop the keyboard focus from the button (clicks are ignored then)
      cta.disabled = !state.ready && !this.adding;
      cta.setAttribute('aria-disabled', state.ready ? 'false' : 'true');
      cta.classList.toggle('is-ready', state.ready);
      cta.classList.toggle('is-loading', this.adding);
      if (this.adding) {
        cta.setAttribute('aria-busy', 'true');
      } else {
        cta.removeAttribute('aria-busy');
      }
    }

    _formatMoney(cents) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }

    _showError(message) {
      this.els.error.textContent = message;
      this.els.error.hidden = false;
    }

    _clearError() {
      this.els.error.textContent = '';
      this.els.error.hidden = true;
    }

    /*============================================================================
      Discount (display only: nothing is applied to the cart)
    ==============================================================================*/

    // Reads data-discount-type (percent | fixed_amount), data-discount-percent, data-discount-amount (cents)
    _readDiscount() {
      this.discountType = this.dataset.discountType === 'fixed_amount' ? 'fixed_amount' : 'percent';
      this.discountPercent = parseFloat(this.dataset.discountPercent) || 0;
      this.discountAmount = parseInt(this.dataset.discountAmount, 10) || 0;
    }

    // Price after the discount: the percentage, or the fixed amount, off `cents`
    _discounted(cents) {
      if (this.discountType === 'fixed_amount') return Math.max(0, cents - this.discountAmount);
      return Math.round((cents * (100 - this.discountPercent)) / 100);
    }

    // Bundle total: a percentage comes off the second item only, a fixed amount off the sum of both items
    _bundleTotal(first, second) {
      if (this.discountType === 'fixed_amount') return this._discounted(first + second);
      return first + this._discounted(second);
    }

    /*============================================================================
      Add to cart
    ==============================================================================*/

    // Converted-size line item property, same as theme.js' ProductForm#addItemFromForm adds for the main form:
    // name = the unit's size label (e.g. "US Men Size"), value = the converted size. Nothing in EU.
    // Main product: read from the main form's active converted size item, exactly like the theme does.
    _firstConvertedSize() {
      var active = (this.section || document).querySelector(selectors.activeConvertedSize);
      if (!active || !active.dataset.size) return null;
      return { name: active.dataset.size, value: active.innerText.trim() };
    }

    // Same property for the widget's own item, from its size in the unit it is displayed in
    _secondConvertedSize() {
      var format = this._sizeFormat();
      if (format === 'eu' || !this.size || !this._hasSizeInFormat(this.size, format)) return null;

      var names = this.conversion.propertyNames || {};
      if (!names[format]) return null;
      return { name: names[format], value: String(this._sizeText(this.size, format)) };
    }

    _bundleId() {
      return 'bw-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    }

    _addToCart() {
      var state = this._ctaState();
      var secondVariant = this._secondVariant();
      if (this.adding || !state.ready || !this.firstVariant || !secondVariant) return;

      // The main form can change while the request is running
      var firstId = this.firstVariant.id;

      this.adding = true;
      this._clearError();
      this._renderCta();
      this._renderStatus();

      // Underscore-prefixed keys are hidden in the cart and checkout; the shared id ties the two lines together
      var bundleId = this._bundleId();
      var properties = (converted) => {
        var props = { _bundle_id: bundleId, _bundle_type: this.bundleType };
        if (converted) props[converted.name] = converted.value;
        return props;
      };

      this._submitItems(
        [
          { id: firstId, quantity: 1, properties: properties(this._firstConvertedSize()) },
          { id: secondVariant.id, quantity: 1, properties: properties(this._secondConvertedSize()) }
        ],
        bundleId
      )
        .catch((error) => {
          // Only the cart's own message is shown; anything else (network, unexpected response) gets the generic text
          if (!error || !error.isCartError) console.warn(this.localName + ': add to cart failed', error);
          this._showError(error && error.isCartError ? error.message : this.strings.errorAdd);
        })
        .finally(() => {
          this.adding = false;
          if (this.initialized) {
            this._renderCta();
            this._renderStatus();
          }
        });
    }

    // POST the bundle lines in ONE request, then run the theme's add-to-cart success flow. Rejects with an Error
    // marked `isCartError` (its message is shown by the caller) when the cart refuses the items. `items[0]` is the
    // main product's line.
    _submitItems(items, bundleId) {
      return fetch(theme.routes.cartAdd, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items })
      })
        .then((response) =>
          response.json().then(
            (json) => ({ ok: response.ok, json: json }),
            // Not JSON (e.g. an HTML error page)
            () => ({ ok: response.ok, json: null })
          )
        )
        .then((result) => {
          var json = result.json;

          // Failed without a cart message: the caller logs it and shows the generic text
          if (!result.ok && !json) throw new Error('Unexpected response from the cart');

          if (!result.ok || (json && json.status)) {
            var error = new Error(json.description || json.message || this.strings.errorAdd);
            error.isCartError = true;
            throw error;
          }

          // The lines are in the cart from here on (a successful response without a readable body counts too, so a
          // retry cannot add the bundle twice): a failure in the follow-up is logged, not shown as a failed add
          var added = (json && json.items) || [];
          return Promise.resolve()
            .then(() => this._afterAdd(added, bundleId, items[0].id))
            .then(() => this._onAdded(added, bundleId))
            .catch((followUpError) => {
              console.warn(this.localName + ': added to the cart, but the follow-up failed', followUpError);
            });
        });
    }

    // Hook: runs after the lines are in the cart and before the cart is shown (may return a promise)
    _afterAdd() {}

    // Same flow as theme.AjaxProduct#success: cart drawer / cart count listen to this event
    _onAdded(added, bundleId) {
      if (theme.settings.cartType === 'page') {
        window.location = theme.routes.cartPage;
        return;
      }

      document.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          detail: { product: added[0] || null, items: added, bundleId: bundleId }
        })
      );

      if (document.body.classList.contains('template-cart')) {
        window.scrollTo(0, 0);
        location.reload();
      }
    }
  }

  class BundleSecondPairWidget extends BundleWidget {
    constructor() {
      super();
      this.bundleType = 'variant-pair';
    }

    _prepare() {
      super._prepare();

      // A swatch of the main product's own colour needs no request
      this.cache.set(this.firstProduct.handle, this.firstProduct);
    }
  }

  class BundleAccessoryWidget extends BundleWidget {
    constructor() {
      super();
      this.bundleType = 'accessory';

      this.mainSize = null; // main form's selected size (native value) the accessory size was last matched to
      this.sizeSource = null; // how the accessory size was set: 'auto' (matched to the main size) | 'manual'
    }

    /*============================================================================
      Main product size -> accessory size matching
    ==============================================================================*/

    _onFirstVariantUpdated() {
      this._syncMainSize();
    }

    // The accessory size is re-matched only when the main size actually changes, so a manual choice survives
    // unrelated main form events (and the catch-up read on window load) until the main size changes again
    _syncMainSize() {
      var size = this.firstVariant ? this.firstVariant.size : null;
      if (size === this.mainSize) return;

      this.mainSize = size;
      this._matchSize();
    }

    _matchSize() {
      if (!this.mainSize || !this._needsSize()) return;

      var match = this._findMatchingSize(this.product, this.mainSize);
      this.sizeSource = 'auto';
      this.desiredSize = match ? match.value : null;
      this.size = match && this._sizeSelectable(match.value) ? match.value : null;
    }

    /*
      The accessory size that corresponds to `target` (a native main size): an exact value (case-insensitive) first,
      otherwise a range value containing it ("39/40", "39-40" contain 39, 39.5 and 40). A half size between two ranges
      (e.g. 38.5 with "37/38" and "39/40") is rounded UP. Named sizes (Small, Medium, ...) count as their numeric range
      from NAMED_SIZE_RANGES, on either side. Prefers an in stock entry; returns null when nothing matches.
    */
    _findMatchingSize(product, target) {
      if (!product || !product.sizes.length) return null;

      var normalized = String(target).trim().toLowerCase();
      var pool = product.sizes.filter((item) => String(item.value).trim().toLowerCase() === normalized);

      if (!pool.length) {
        var targetNumber = this._numbers(target)[0];
        if (targetNumber !== undefined) {
          pool = this._rangesContaining(product.sizes, targetNumber);
          if (!pool.length && targetNumber !== Math.floor(targetNumber)) {
            pool = this._rangesContaining(product.sizes, Math.ceil(targetNumber));
          }
        }
      }

      return pool.find((item) => item.available) || pool[0] || null;
    }

    _rangesContaining(sizes, number) {
      return sizes.filter((item) => {
        var numbers = this._numbers(item.value);
        if (!numbers.length) return false;
        return number >= Math.min.apply(null, numbers) && number <= Math.max.apply(null, numbers);
      });
    }

    // Numbers a size value stands for: the ones written in it ("39/40" -> 39, 40), or the range of a named size
    // ("Medium" -> 41, 42) when it contains no digits
    _numbers(value) {
      var numbers = (String(value).match(/\d+(?:[.,]\d+)?/g) || []).map((n) => parseFloat(n.replace(',', '.')));
      if (numbers.length) return numbers;

      var named = NAMED_SIZE_RANGES[String(value).toLowerCase().replace(/[^a-z]/g, '')];
      return named ? named.slice() : [];
    }

    /*============================================================================
      Accessory colour / size selection
    ==============================================================================*/

    // Keep the matched / picked size when the (new) colour has it in stock. Otherwise a size that was matched to the
    // main size is matched again (the new colour may name its ranges differently); a manual pick stays the wanted
    // size and is reported as unavailable in this colour.
    _applyDesiredSize() {
      super._applyDesiredSize();
      if (!this.size && this.sizeSource === 'auto') this._matchSize();
    }

    _selectSize(value) {
      var applied = super._selectSize(value);
      if (applied) this.sizeSource = 'manual';
      return applied;
    }

    /*
      Ties the accessory line to the main product line with the same `_upsell_for*` properties the post add-to-cart
      upsell sets (PostAtcUpsellCore#_parentProperties), so the cart treats it as that product's upsell. The main
      line's key only exists once it is in the cart, hence a follow-up /cart/change.js on the accessory line.
      A failure here is not shown: both items are already in the cart.
    */
    _afterAdd(added, bundleId, mainVariantId) {
      var inBundle = (item) => item.properties && item.properties._bundle_id === bundleId;
      var main = added.find((item) => inBundle(item) && item.variant_id === mainVariantId);
      var accessory = added.find((item) => inBundle(item) && item !== main);
      if (!main || !accessory || !accessory.key) return;

      var name = main.product_title || '';
      if (main.variant_title) name += ' - ' + main.variant_title;

      var properties = Object.assign({}, accessory.properties, {
        _upsell_for: name,
        _upsell_for_variant_id: String(main.variant_id || main.id || ''),
        _upsell_for_key: main.key || ''
      });

      return fetch(theme.routes.cartChange, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: accessory.key, quantity: accessory.quantity || 1, properties: properties })
      })
        .then((response) => {
          if (!response.ok) console.warn(this.localName + ': could not link the accessory to the main item');
        })
        .catch((error) => {
          console.warn(this.localName + ': could not link the accessory to the main item', error);
        });
    }
  }

  customElements.define('bundle-second-pair-widget', BundleSecondPairWidget);
  customElements.define('bundle-accessory-widget', BundleAccessoryWidget);
})();
